import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  apiRequest,
  formatCurrency,
  formatDate,
  getProfileImage,
} from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";

import {
  creditTypeLabel,
  creditStatusLabel,
  toneForCreditStatus,
  toneForCreditType,
} from "../../../shared/Utils";

const amountClass = (type = "") => {
  if (type === "credit_used") return "credit-amount-used";
  if (type === "payment_received" || type === "credit_reversed")
    return "credit-amount-paid";
  return "";
};

const CreditHistoryDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTransaction = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const result = await apiRequest(`/credit/transactions/${id}`, "GET");
      if (result.success && result.transaction) {
        setTransaction(result.transaction);
      } else {
        throw new Error(result.message || "Transaction not found");
      }
    } catch (error: any) {
      Swal.fire(
        "Error",
        error.message || "Unable to load transaction",
        "error",
      );
      navigate("/credit-history");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    void fetchTransaction();
  }, [fetchTransaction]);

  if (isLoading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading Transaction Details…</h2>
      </div>
    );
  }

  if (!transaction) return null;

  const user = transaction.user;
  const order = transaction.order;
  const createdBy = transaction.createdBy;
  const historyLink = user?._id
    ? `/credit-history?userId=${user._id}`
    : "/credit-history";
  const settled = Number(transaction.settledAmount || 0);
  const reversed = Number(transaction.reversedAmount || 0);
  const shortRef = `#${String(transaction._id).slice(-8).toUpperCase()}`;

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to={historyLink} className="btn-sm btn-outline back-link">
            ← Back to Credit History
          </Link>
          <div className="page-eyebrow">Credit Transaction</div>
          <h1 className="page-title">{creditTypeLabel(transaction.type)}</h1>
          <div className="page-sub">
            {formatDate(transaction.createdAt)} · {shortRef}
          </div>
        </div>
        <div className="page-hdr-right">
          <span
            className={`status-badge ${toneForCreditStatus(transaction.status)}`}
          >
            {creditStatusLabel(transaction.status)}
          </span>
        </div>
      </div>

      <section className="table-card credit-due-flow">
        <div className="credit-due-step">
          <label>Due Before</label>
          <strong>{formatCurrency(transaction.balanceBefore)}</strong>
        </div>
        <span className="credit-due-arrow" aria-hidden="true">
          →
        </span>
        <div className="credit-due-step is-entry">
          <label>This Entry</label>
          <strong className={amountClass(transaction.type)}>
            {formatCurrency(transaction.amount)}
          </strong>
          <small>
            <span
              className={`status-badge ${toneForCreditType(transaction.type)}`}
            >
              {creditTypeLabel(transaction.type)}
            </span>
          </small>
        </div>
        <span className="credit-due-arrow" aria-hidden="true">
          →
        </span>
        <div className="credit-due-step">
          <label>Due After</label>
          <strong>{formatCurrency(transaction.balanceAfter)}</strong>
        </div>
      </section>

      <div className="detail-grid-3">
        <div className="table-card info-card">
          <h3 className="info-card-title">Customer</h3>
          {user ? (
            <>
              <Link to={`/users/${user._id}`} className="img-name-cell">
                <AppImage
                  src={getProfileImage(user)}
                  alt={user.name}
                  crossOrigin="anonymous"
                />
                <div>
                  <strong>{user.name}</strong>
                  <small className="table-muted">{user.email || "—"}</small>
                  {user.phone ? (
                    <small className="table-muted">{user.phone}</small>
                  ) : null}
                </div>
              </Link>
              <div className="profile-meta">
                <div className="profile-meta-row">
                  <span>Credit Limit</span>
                  <strong>{formatCurrency(user.creditLimit)}</strong>
                </div>
                <div className="profile-meta-row">
                  <span>Used / Due</span>
                  <strong>{formatCurrency(user.creditOutstanding)}</strong>
                </div>
                <div className="profile-meta-row">
                  <span>Available Credit</span>
                  <strong>{formatCurrency(user.availableCredit || 0)}</strong>
                </div>
              </div>
              <div className="info-card-actions">
                <Link to={`/users/${user._id}`} className="btn-sm btn-outline">
                  View Profile
                </Link>
                <Link to={historyLink} className="btn-sm btn-outline">
                  View all
                </Link>
              </div>
            </>
          ) : (
            <p className="credit-txn-empty">Customer not available</p>
          )}
        </div>

        <div className="table-card info-card">
          <h3 className="info-card-title">Linked Order</h3>
          {order?._id ? (
            <div className="info-list">
              <p>
                <strong>Order Code:</strong>{" "}
                <Link to={`/orders/${order._id}`} className="order-code">
                  {order.orderCode}
                </Link>
              </p>
              <p>
                <strong>Order Total:</strong> {formatCurrency(order.totalPrice)}
              </p>
              {order.orderStatus ? (
                <p>
                  <strong>Order Status:</strong>{" "}
                  <span className="status-badge muted">
                    {order.orderStatus}
                  </span>
                </p>
              ) : null}
              {order.createdAt ? (
                <p>
                  <strong>Order Date:</strong> {formatDate(order.createdAt)}
                </p>
              ) : null}
              <div className="info-card-actions">
                <Link
                  to={`/orders/${order._id}`}
                  className="btn-sm btn-outline"
                >
                  View Order
                </Link>
              </div>
            </div>
          ) : (
            <p className="credit-txn-empty">No order linked to this entry.</p>
          )}
        </div>

        <div className="table-card info-card">
          <h3 className="info-card-title">Recorded By</h3>
          <div className="info-list">
            {createdBy ? (
              <>
                <p>
                  <strong>Admin:</strong> {createdBy.name}
                </p>
                <p>
                  <strong>Email:</strong> {createdBy.email || "—"}
                </p>
              </>
            ) : (
              <p>System generated entry</p>
            )}
            <p>
              <strong>Created:</strong> {formatDate(transaction.createdAt)}
            </p>
            {transaction.updatedAt &&
            transaction.updatedAt !== transaction.createdAt ? (
              <p>
                <strong>Updated:</strong> {formatDate(transaction.updatedAt)}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>Entry Details</h2>
          <p>Reference, notes and extra amounts for this transaction</p>
        </div>
        <div className="detail-kv-grid">
          <div className="detail-kv-item">
            <label>Activity</label>
            <strong>
              <span
                className={`status-badge ${toneForCreditType(transaction.type)}`}
              >
                {creditTypeLabel(transaction.type)}
              </span>
            </strong>
          </div>
          <div className="detail-kv-item">
            <label>Status</label>
            <strong>
              <span
                className={`status-badge ${toneForCreditStatus(transaction.status)}`}
              >
                {creditStatusLabel(transaction.status)}
              </span>
            </strong>
          </div>
          {settled > 0 && (
            <div className="detail-kv-item">
              <label>Settled Amount</label>
              <strong>{formatCurrency(settled)}</strong>
            </div>
          )}
          {reversed > 0 && (
            <div className="detail-kv-item">
              <label>Reversed Amount</label>
              <strong>{formatCurrency(reversed)}</strong>
            </div>
          )}
          <div className="detail-kv-item">
            <label>Payment Reference</label>
            <strong>{transaction.paymentReference || "—"}</strong>
          </div>
          <div className="detail-kv-item">
            <label>Note</label>
            <strong>{transaction.note || "—"}</strong>
          </div>
          <div className="detail-kv-item">
            <label>Transaction ID</label>
            <strong className="break-all">{transaction._id}</strong>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreditHistoryDetail;
